// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import React from 'react'
/**
 * Step09MedicalWishes - Medische wensen (wizard stap)
 * V37-P1: DNAR-waarschuwing, wilsverklaring documentstatus (3 keuzes),
 *         huisarts volledige velden, VP-keuzehulp.
 */
import { useCallback, useState, useEffect } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Card }       from '@/components/ui/Card'
import { Textarea }   from '@/components/ui/Textarea'
import { Input }      from '@/components/ui/Input'
import { RadioGroup } from '@/components/ui/RadioGroup'
import { WizardStepShell } from './WizardStepShell'
import { loadMedicalWishes, saveMedicalWishes, EMPTY_MEDICAL, type MedicalWishesData } from '@/services/wishes'
import { loadTrustedPersons } from '@/services/trustedPerson'
import { useAuthStore } from '@/store'
import type { SaveStatus } from '@/hooks/useAutosave'

const RESUS = [
  { value: 'yes',     label: 'Ja, reanimeer mij' },
  { value: 'no',      label: 'Nee, niet reanimeren (DNAR)' },
  { value: 'discuss', label: 'Bespreek met mijn arts' },
]

const LIVING_WILL_STATUS = [
  { value: 'yes',     label: 'Ja, ik heb een schriftelijke wilsverklaring' },
  { value: 'pending', label: 'In voorbereiding' },
  { value: 'no',      label: 'Nee' },
]

export function Step09MedicalWishes({ onSaveStatus, saveRef }: { onSaveStatus: (s: SaveStatus) => void; saveRef?: React.MutableRefObject<(() => Promise<void>) | null> }) {
  const { userId }  = useAuthStore()
  const load = useCallback(loadMedicalWishes, [])
  const save = useCallback(saveMedicalWishes, [])

  const [primaryVP, setPrimaryVP] = useState('')
  const [reserveVP, setReserveVP] = useState('')

  useEffect(() => {
    if (!userId) return
    loadTrustedPersons(userId).then(vps => {
      const active  = vps.filter(v => v.confirmationStatus !== 'REVOKED')
      const primary = active.find(v => v.role === 'PRIMARY')
      const reserve = active.find(v => v.role === 'RESERVE')
      if (primary) setPrimaryVP(primary.firstName)
      if (reserve) setReserveVP(reserve.firstName)
    })
  }, [userId])

  return (
    <WizardStepShell emptyData={EMPTY_MEDICAL} load={load} save={save} onSaveStatus={onSaveStatus} saveRef={saveRef}>
      {(data, upd) => (
        <div className="flex flex-col gap-5">
          <div>
            <h2 className="text-xl font-semibold text-ink mb-1">Medische wensen</h2>
            <p className="text-sm text-ink-3">Zodat uw naasten weten wat u wel en niet wilt als u zelf niet meer kunt aangeven.</p>
          </div>

          {/* Behandelwensen */}
          <Card>
            <div className="rounded-xl border border-l-4 border-status-infoBorder border-l-status-infoText bg-status-infoBg px-4 py-3.5 mb-3">
              <h3 className="text-sm font-semibold text-ink mb-1.5">Uw wensen rondom behandeling</h3>
              <p className="text-xs text-ink-3 leading-relaxed">
                Door dementie, een beroerte, coma of ernstige ziekte kan het voorkomen dat u zelf niet meer kunt
                aangeven wat u wilt. Uw naasten en artsen moeten dan weten wat uw wensen zijn.
                Leg hier vast wat voor u belangrijk is.
              </p>
            </div>
            <Textarea value={data.treatmentPreferences}
              onChange={e => upd('treatmentPreferences', e.target.value)}
              placeholder="Bijv: Ik wil geen kunstmatige beademing als herstel niet realistisch is. Bij dementie in vergevorderd stadium wil ik geen intensieve behandeling meer..."
              maxLength={800} />
          </Card>

          {/* Reanimatie */}
          <Card>
            <h3 className="text-sm font-semibold text-ink mb-3">Reanimatie</h3>
            <RadioGroup name="resus" options={RESUS} value={data.resuscitation}
              onChange={v => upd('resuscitation', v as MedicalWishesData['resuscitation'])} />

            {/* DNAR-waarschuwing (V37-P1) */}
            {data.resuscitation === 'no' && (
              <div className="mt-3 flex items-start gap-2.5 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <p className="text-xs font-semibold text-amber-800 mb-0.5">Belangrijk</p>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    Deze keuze in WilsApp legt uw persoonlijke wens vast, maar is geen officiële medische opdracht.
                    Bespreek een niet-reanimerenwens altijd met uw huisarts en leg deze schriftelijk vast.
                    Informeer ook uw naasten hierover.
                  </p>
                </div>
              </div>
            )}
          </Card>

          {/* Schriftelijke wilsverklaring (V37-P1) */}
          <Card>
            <h3 className="text-sm font-semibold text-ink mb-1">Schriftelijke wilsverklaring</h3>
            <p className="text-xs text-ink-3 mt-1 leading-relaxed mb-3">
              Een schriftelijke wilsverklaring kan belangrijk zijn wanneer u zelf niet meer kunt aangeven
              welke medische behandelingen u wel of niet wilt. Voor medische besluitvorming adviseren wij
              deze verklaring te bespreken met uw huisarts en een ondertekend exemplaar te bewaren.{' '}
              <a href="https://www.rijksoverheid.nl/vraag-en-antwoord/levenseinde-en-euthanasie/wilsverklaring-opstellen"
                 target="_blank" rel="noopener noreferrer"
                 className="text-sage-600 underline underline-offset-2 hover:text-sage-800">
                Rijksoverheid →
              </a>
            </p>

            {/* Informatieblok (V37-P1) */}
            <div className="flex items-start gap-2.5 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 mb-4">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-xs text-amber-700 leading-relaxed">
                <strong className="text-amber-800">Let op:</strong> WilsApp legt uw wensen vast als persoonlijke
                leidraad voor uw naasten. Voor officiële medische besluitvorming zijn uw huisarts, specialist
                of notaris bepalend.
              </p>
            </div>

            {/* Documentstatus (V37-P1) - 3 keuzes */}
            <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider mb-2">
              Heeft u een schriftelijke wilsverklaring?
            </p>
            <div className="flex flex-col gap-2 mb-3">
              {LIVING_WILL_STATUS.map(opt => (
                <button key={opt.value} type="button"
                  onClick={() => upd('livingWillExists', opt.value)}
                  className={`flex items-center gap-3 rounded-xl border-2 px-4 py-2.5 text-left transition-all ${
                    data.livingWillExists === opt.value
                      ? 'border-sage-600 bg-sage-50'
                      : 'border-sand-300 hover:border-sage-300'
                  }`}>
                  <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                    data.livingWillExists === opt.value ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
                  }`} />
                  <span className="text-sm text-ink">{opt.label}</span>
                </button>
              ))}
            </div>

            {/* Locatievelden bij Ja of In voorbereiding */}
            {(data.livingWillExists === 'yes' || data.livingWillExists === 'pending') && (
              <div className="flex flex-col gap-3 border-t border-sand-200 pt-3 animate-slide-down">
                <Input label="Waar wordt deze bewaard?"
                  value={data.livingWillLocation}
                  onChange={e => upd('livingWillLocation', e.target.value)}
                  placeholder="Bijv: thuis in rode map, bij huisarts, bij notaris..." />
                <Input label="Notaris (optioneel)"
                  value={data.livingWillNotaris}
                  onChange={e => upd('livingWillNotaris', e.target.value)}
                  placeholder="Naam notaris of kantoor" />
              </div>
            )}
          </Card>

          {/* Medisch vertegenwoordiger - met VP-keuzehulp */}
          <Card>
            <h3 className="text-sm font-semibold text-ink mb-1">Medisch vertegenwoordiger</h3>
            <p className="text-xs text-ink-4 leading-relaxed mb-4">
              Wie mag namens u met artsen spreken als u dat zelf niet meer kunt?
              Dit kan dezelfde persoon zijn als uw vertrouwenspersoon, maar dat hoeft niet.
            </p>

            {(primaryVP || reserveVP) && (
              <div className="flex flex-col gap-2 mb-4">
                {primaryVP && (
                  <button type="button"
                    onClick={() => upd('proxySource', 'primary_vp' as MedicalWishesData['proxySource'])}
                    className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                      data.proxySource === 'primary_vp' ? 'border-sage-600 bg-sage-50' : 'border-sand-300 hover:border-sage-300'
                    }`}>
                    <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                      data.proxySource === 'primary_vp' ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
                    }`} />
                    <span className="text-sm text-ink">Eerste vertrouwenspersoon - {primaryVP}</span>
                  </button>
                )}
                {reserveVP && (
                  <button type="button"
                    onClick={() => upd('proxySource', 'reserve_vp' as MedicalWishesData['proxySource'])}
                    className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                      data.proxySource === 'reserve_vp' ? 'border-sage-600 bg-sage-50' : 'border-sand-300 hover:border-sage-300'
                    }`}>
                    <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                      data.proxySource === 'reserve_vp' ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
                    }`} />
                    <span className="text-sm text-ink">Tweede vertrouwenspersoon - {reserveVP}</span>
                  </button>
                )}
                <button type="button"
                  onClick={() => upd('proxySource', 'other' as MedicalWishesData['proxySource'])}
                  className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                    data.proxySource === 'other' || (!['primary_vp','reserve_vp'].includes(data.proxySource) && data.proxySource !== '')
                      ? 'border-sage-600 bg-sage-50' : 'border-sand-300 hover:border-sage-300'
                  }`}>
                  <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                    data.proxySource === 'other' || (!['primary_vp','reserve_vp'].includes(data.proxySource) && data.proxySource !== '')
                      ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
                  }`} />
                  <span className="text-sm text-ink">Andere persoon invoeren</span>
                </button>
              </div>
            )}

            {(data.proxySource === 'primary_vp' || data.proxySource === 'reserve_vp') && (
              <div className="rounded-xl bg-sage-50 border border-sage-100 px-4 py-3">
                <p className="text-xs text-sage-700">
                  {data.proxySource === 'primary_vp' ? primaryVP : reserveVP} is aangewezen als uw medisch vertegenwoordiger.
                  Zorg dat zij hiervan op de hoogte zijn.
                </p>
              </div>
            )}

            {(data.proxySource === 'other' ||
              (!['primary_vp','reserve_vp'].includes(data.proxySource) && data.proxySource !== '') ||
              (!primaryVP && !reserveVP)) && (
              <div className="flex flex-col gap-3 animate-slide-down">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input label="Naam" value={data.proxyDecisionMaker}
                    onChange={e => upd('proxyDecisionMaker', e.target.value)}
                    placeholder="Bijv: Jan de Vries" />
                  <Input label="Relatie" value={(!['primary_vp','reserve_vp','other',''].includes(data.proxySource)) ? data.proxySource : ''}
                    onChange={e => upd('proxySource', e.target.value as MedicalWishesData['proxySource'])}
                    placeholder="Bijv: partner, kind, vriend" />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input label="Telefoonnummer" type="tel" value={data.proxyPhone}
                    onChange={e => upd('proxyPhone', e.target.value as MedicalWishesData['proxyPhone'])}
                    placeholder="Bijv: 06-12345678" />
                  <Input label="E-mailadres (optioneel)" type="email" value={data.proxyEmail}
                    onChange={e => upd('proxyEmail', e.target.value as MedicalWishesData['proxyEmail'])}
                    placeholder="naam@mail.nl" />
                </div>
              </div>
            )}

            {!primaryVP && !reserveVP && (
              <p className="text-xs text-ink-4 mt-3">
                Tip: als u een vertrouwenspersoon aanwijst in stap Vertrouwenspersoon, kunt u die hier snel selecteren.
              </p>
            )}
          </Card>

          {/* Huisarts - volledig (V37-P1) */}
          <Card>
            <h3 className="text-sm font-semibold text-ink mb-3">Uw huisarts</h3>
            <div className="flex flex-col gap-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="Naam huisarts" value={data.gpName}
                  onChange={e => upd('gpName', e.target.value)} placeholder="Dr. Jansen" />
                <Input label="Praktijknaam" value={data.gpPractice}
                  onChange={e => upd('gpPractice', e.target.value)} placeholder="Huisartsenpraktijk De Hoek" />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="Telefoonnummer" type="tel" value={data.gpPhone}
                  onChange={e => upd('gpPhone', e.target.value)} placeholder="020-1234567" />
                <Input label="Plaats" value={data.gpCity}
                  onChange={e => upd('gpCity', e.target.value)} placeholder="Amsterdam" />
              </div>
            </div>
            <p className="text-xs text-ink-4 mt-3">
              Controleer regelmatig of deze gegevens nog actueel zijn.
            </p>
          </Card>
        </div>
      )}
    </WizardStepShell>
  )
}
